import {
  Box,
  Drawer,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AurButton from '../../common/aur-button/AurButton';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import {
  getKpiPanelDatasetsColumns,
  getKpiPanelProductionColumns,
  getKpiPanelProjectColumns,
} from '../../../utils/kpiPanelUtils';
import {useNavigate} from 'react-router-dom';
import {GridRowParams, GridSortModel} from '@mui/x-data-grid';
import {
  ONLY_DATE_SHOW_FORMAT,
  TABLE_PAGE_SIZE_OPTION,
} from '../../../constants/constants';
import {getFilteredProjectList} from '../../../api/ClientManagementService';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  getDataSetProcessed,
  getProductionsCompleted,
} from '../../../api/ReportingManagement';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  setReportPageOpenFrom,
  storeSelectedDataSet,
} from '../../../redux/slices/reportsSlice';
import {GA_REGIONS} from '../../../constants/analyticsConstants';
import {setReportSrc} from '../../../redux/slices/analyticsSlice';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';

interface IDataSet {
  datasetsProcessed: number;
  metricsUpdatedOn: string;
  projectsAdded: number;
  productionCompleted: number;
  recentDays: number;
}
interface IKpiPanel {
  setShowDrawer: (arg0: boolean) => void;
  tabValue: number;
  setTabValue: (arg0: number) => void;
  showDrawer: boolean;
  dataSet?: IDataSet | null;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
    tabIndex: 0,
  };
}

const KpiPanel = (props: IKpiPanel) => {
  const {setShowDrawer, tabValue, setTabValue, showDrawer, dataSet} = props;
  const [projectAddedCurrentPage, setProjectAddedCurrentPage] =
    useState<number>(1);
  const [datasetProcessedCurrentPage, setDatasetProcessedCurrentPage] =
    useState<number>(1);
  const [productionsCompletedcurrentPage, setProductionsCompletedcurrentPage] =
    useState<number>(1);

  const [projectAddedItemsPerPage, setProjectAddedItemsPerPage] =
    useState<number>(25);
  const [datasetProcessedItemsPerPage, setDatasetProcessedItemsPerPage] =
    useState<number>(25);
  const [
    productionsCompletedItemsPerPage,
    setProductionsCompletedItemsPerPage,
  ] = useState<number>(25);
  //commenting this code as this is unused
  // const [currentPage, setCurrentPage] = useState<number>(1);
  // const [itemsPerPage, setItemsPerPage] = useState<number>(25);
  // const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);

  const [projectAdded, setProjectAdded] = useState([]);
  const [totalProjectAdded, setTotalProjectAdded] = useState(0);
  const [projectAddedSortModel, setProjectAddedSortModel] =
    useState<GridSortModel>([]);

  const [datasetsProcessedRows, setDatasetsProcessedRows] = useState([]);
  const [totalDatasetsProcessedRows, setTotalDatasetsProcessedRows] =
    useState(0);
  const [datasetsProcessedSortModel, setDatasetsProcessedSortModel] =
    useState<GridSortModel>([]);

  const [productionsCompletedRows, setProductionsCompletedRows] = useState([]);
  const [totalProductionsCompletedRows, setTotalProductionsCompletedRows] =
    useState(0);
  const [productionsCompletedSortModel, setProductionsCompletedSortModel] =
    useState<GridSortModel>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient
  );

  const addDaysToDate = (date: Date, days: number): string => {
    const newDate = new Date(date);
    newDate.setUTCDate(newDate.getUTCDate() + days);
    newDate.setUTCHours(0, 0, 0, 0);
    return newDate.toISOString();
  };

  const fetchProjectsAdded = async () => {
    const orderBy = projectAddedSortModel[0]?.field
      ? `$orderby=${projectAddedSortModel[0]?.field} ${projectAddedSortModel[0]?.sort}&`
      : '';

    const targetDate = addDaysToDate(new Date(), -(dataSet?.recentDays ?? 0));
    // OData query
    const params = `?${orderBy}$top=${projectAddedItemsPerPage}&$skip=${(projectAddedCurrentPage - 1) * projectAddedItemsPerPage}&$filter=createdOn ge ${targetDate}`;
    setIsLoading(true);
    setIsFailedToLoadContent(false);
    try {
      const response = await getFilteredProjectList(
        selectedClient.clientId,
        params
      );
      setTotalProjectAdded(response.data.data.totalCount ?? 0);
      setProjectAdded(response.data.data.data ?? []);
    } catch (error) {
      console.warn(error);
      setIsFailedToLoadContent(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataSetProcessed = async () => {
    const params = `SortColumn=${datasetsProcessedSortModel[0]?.field ?? ''}&SortOrder=${datasetsProcessedSortModel[0]?.sort ?? ''}&pageNumber=${datasetProcessedCurrentPage}&pageSize=${datasetProcessedItemsPerPage}&lastDays=${dataSet?.recentDays}&clientId=${selectedClient.clientId}`;
    setIsLoading(true);
    setIsFailedToLoadContent(false);
    try {
      const response = await getDataSetProcessed(params);
      const responseData = response?.data?.data.map((item: any, index: any) => {
        return {
          ...item,
          id: index,
        };
      });

      setTotalDatasetsProcessedRows(
        response.data.meta.pagination.totalItems ?? 0
      );
      setDatasetsProcessedRows(responseData ?? []);
    } catch (error) {
      console.warn(error);

      setIsFailedToLoadContent(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductionCompleted = async () => {
    const params = `SortColumn=${productionsCompletedSortModel[0]?.field ?? ''}&SortOrder=${productionsCompletedSortModel[0]?.sort ?? ''}&pageNumber=${productionsCompletedcurrentPage}&pageSize=${productionsCompletedItemsPerPage}&lastDays=${dataSet?.recentDays}&clientId=${selectedClient.clientId}`;

    setIsLoading(true);
    setIsFailedToLoadContent(false);
    try {
      const response = await getProductionsCompleted(params);
      const responseData = response?.data?.data.map((item: any, index: any) => {
        return {
          ...item,
          id: index,
        };
      });
      setTotalProductionsCompletedRows(
        response.data.meta.pagination.totalItems ?? 0
      );
      setProductionsCompletedRows(responseData ?? []);
    } catch (error) {
      console.warn(error);

      setIsFailedToLoadContent(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 1) {
      fetchDataSetProcessed();
    } else if (tabValue === 2) {
      fetchProductionCompleted();
    } else {
      fetchProjectsAdded();
    }
  }, [
    datasetProcessedItemsPerPage,
    datasetProcessedCurrentPage,
    datasetsProcessedSortModel,
    productionsCompletedItemsPerPage,
    productionsCompletedcurrentPage,
    productionsCompletedSortModel,
    projectAddedItemsPerPage,
    projectAddedCurrentPage,
    projectAddedSortModel,
    tabValue,
  ]);

  const datasetPanel = [
    ...getKpiPanelDatasetsColumns(),
    {
      field: 'arrow',
      headerName: '',
      sortable: false,
      headerClassName: 'no-separator',
      width: 44,
      isBorderNotRequired: true,
      renderCell: () => (
        <IconButton>
          <img src="/assests/images/chev_right.svg" />
        </IconButton>
      ),
    },
  ];

  const navigateToReport = (url: string) => {
    dispatch(setReportSrc(GA_REGIONS.BODY));
    dispatch(setReportPageOpenFrom('/welcome-single-client'));
    navigate(url);
  };

  const productionPanel = [
    ...getKpiPanelProductionColumns(),
    {
      field: 'arrow',
      headerName: '',
      sortable: false,
      width: 44,
      headerClassName: 'no-separator',
      isBorderNotRequired: true,
      renderCell: () => (
        <IconButton onClick={() => navigateToReport('/reports')}>
          <img src="/assests/images/chev_right.svg" />
        </IconButton>
      ),
    },
  ];

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDatasetProcessedRowClick = (params: GridRowParams) => {
    // Get the currently selected row
    const selectedRow = params.row;

    // Store information about the currently selected item for the reports page
    dispatch(
      storeSelectedDataSet({
        projectId: selectedRow.projectId ?? '',
        projectName: selectedRow.projectName ?? '',
        workspaceId: '', // <-- this gets populated on /reports page
        workspaceName: selectedRow.workspaceName ?? '',
        workspaceDataSetName: selectedRow.datasetName ?? '',
        worksapceDataSetMediaId: '',
      })
    );

    // Navigate to the specified tab on the reports page
    navigateToReport('/reports?tab=processed-data');
  };

  const handleProductionsCompletedRowClick = (params: GridRowParams) => {
    // Get the currently selected row
    const selectedRow = params.row;

    // Store information about the currently selected item for the reports page
    dispatch(
      storeSelectedDataSet({
        projectId: selectedRow.projectId ?? '',
        projectName: selectedRow.projectName ?? '',
        workspaceId: '', // <-- this gets populated on /reports page
        workspaceName: selectedRow.workspaceName ?? '',
        workspaceDataSetName: selectedRow.datasetName ?? '',
        worksapceDataSetMediaId: '',
      })
    );

    // Navigate to the specified tab on the reports page
    navigateToReport('/reports?tab=production-metric&hideModal=true');
  };

  const handleProjectAddedRowClick = (params: GridRowParams) => {
    // Get the currently selected row
    const selectedRow = params.row;

    // Store information about the currently selected item for the reports page
    dispatch(
      storeSelectedDataSet({
        projectId: selectedRow.projectId ?? '',
        projectName: selectedRow.projectName ?? '',
        workspaceId: '',
        workspaceName: '',
        workspaceDataSetName: '',
        worksapceDataSetMediaId: '',
      })
    );

    // Navigate to the specified tab on the reports page
    navigateToReport('/reports?tab=intake-audit');
  };

  return (
    <Drawer
      open={showDrawer}
      anchor="right"
      className="kpi-panel"
      onClose={() => setShowDrawer(false)}
    >
      <Grid className="drawer-container">
        <Grid container className="drawer-header drawer-header-fixed">
          <AurButton
            type="exit"
            label={t('buttons.close')}
            title={t('buttons.close')}
            size="Medium"
            onClick={() => setShowDrawer(false)}
            dataTestId={'closeBtn-test'}
          />
        </Grid>
        <Grid container className="drawer-header">
          <Grid xs={12}>
            <Typography className="typo-text headline2_size recent-activity">
              {t('kipPanel.recentActivity')}
            </Typography>
          </Grid>
          <Grid container xs={11} className="sub-text-header">
            <Grid xs={8.3}>
              <Typography className="typo-text body3_size mist-700-color">
                {t('kipPanel.subHeaderText1')}{' '}
                <span className="typo-text label1_size mist-color">
                  {t('kipPanel.last')} {dataSet?.recentDays}{' '}
                  {t('kipPanel.days')}
                </span>
              </Typography>
            </Grid>
            <Grid xs={3.5}>
              <Typography className="typo-text body3_size mist-700-color">
                {t('kipPanel.subHeaderText2')}
                <AurLightTooltip
                  title={dataSet ? t('common.yearMonthDate') : ''}
                >
                  <span className="typo-text label1_size mist-color">
                    {dataSet
                      ? getUTCDateTimeForDisplay(
                          dataSet?.metricsUpdatedOn,
                          ONLY_DATE_SHOW_FORMAT
                        )
                      : t('common.loading')}
                  </span>
                </AurLightTooltip>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Tabs
            value={tabValue}
            onChange={handleChange}
            aria-label="basic tabs example"
            className={`aur-tabs kpi-tabs ${tabValue === 2 ? 'last-tab-selected' : ''}`}
            variant="fullWidth"
          >
            <Tab
              label={
                <>
                  <Typography className="typo-text display3_size">
                    {dataSet?.projectsAdded}
                  </Typography>
                  <Typography className="typo-text body3_size">
                    {t('kipPanel.projectsAdded')}
                  </Typography>
                </>
              }
              {...a11yProps(0)}
            />
            <Tab
              label={
                <>
                  <Typography className="typo-text display3_size">
                    {dataSet?.datasetsProcessed}
                  </Typography>
                  <Typography className="typo-text body3_size">
                    {t('kipPanel.datasetProcessed')}
                  </Typography>
                </>
              }
              {...a11yProps(1)}
            />
            <Tab
              label={
                <>
                  <Typography className="typo-text display3_size">
                    {dataSet?.productionCompleted}
                  </Typography>
                  <Typography className="typo-text body3_size">
                    {t('kipPanel.productionCompleted')}
                  </Typography>
                </>
              }
              {...a11yProps(2)}
            />
          </Tabs>
        </Grid>
        {isLoading && (
          <Box className="blank-area-loader">
            <AurLoadingIndicator show={true} />
          </Box>
        )}
        {tabValue === 0 && !isLoading && (
          <AurDataGrid
            className="kpi-panel-grid"
            rows={projectAdded}
            columns={getKpiPanelProjectColumns()}
            visibleToolTip={true}
            pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
            checkBoxVisible={false}
            entityType={t('common.kpiPanel')}
            getRowId={(row) => row.projectId}
            currentPage={projectAddedCurrentPage}
            setCurrentPage={setProjectAddedCurrentPage}
            itemsPerPage={projectAddedItemsPerPage}
            sortModel={projectAddedSortModel}
            setSortModel={setProjectAddedSortModel}
            setItemsPerPage={setProjectAddedItemsPerPage}
            totalRowCount={totalProjectAdded}
            handleRowClick={handleProjectAddedRowClick}
            noRowsOverlay={
              isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
            }
          />
        )}
        {tabValue === 1 && !isLoading && (
          <AurDataGrid
            className="kpi-panel-grid"
            rows={datasetsProcessedRows}
            columns={datasetPanel}
            pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
            checkBoxVisible={false}
            visibleToolTip={true}
            entityType={t('common.kpiPanel')}
            getRowId={(row) => row.id}
            currentPage={datasetProcessedCurrentPage}
            setCurrentPage={setDatasetProcessedCurrentPage}
            itemsPerPage={datasetProcessedItemsPerPage}
            setItemsPerPage={setDatasetProcessedItemsPerPage}
            totalRowCount={totalDatasetsProcessedRows}
            handleRowClick={handleDatasetProcessedRowClick}
            setSortModel={setDatasetsProcessedSortModel}
            sortModel={datasetsProcessedSortModel}
          />
        )}
        {tabValue === 2 && !isLoading && (
          <AurDataGrid
            className="kpi-panel-grid"
            rows={productionsCompletedRows}
            columns={productionPanel}
            pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
            visibleToolTip={true}
            checkBoxVisible={false}
            entityType={t('common.kpiPanel')}
            getRowId={(row) => row.id}
            currentPage={productionsCompletedcurrentPage}
            setCurrentPage={setProductionsCompletedcurrentPage}
            itemsPerPage={productionsCompletedItemsPerPage}
            setItemsPerPage={setProductionsCompletedItemsPerPage}
            totalRowCount={totalProductionsCompletedRows}
            sortModel={productionsCompletedSortModel}
            setSortModel={setProductionsCompletedSortModel}
            handleRowClick={handleProductionsCompletedRowClick}
          />
        )}
      </Grid>
    </Drawer>
  );
};

export default KpiPanel;
